import React, { useState } from 'react';
import { 
  ProgramDay, 
  ProgramDayExercise, 
  ExerciseProgram, 
  Exercise,
  DailyRecord
} from '../types';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { 
  Plus, Trash2, Edit2, Play, Calendar, Check, X, ChevronDown, ChevronUp, 
  Sliders, Sparkles, AlertCircle, CheckCircle2, FileCode, Dumbbell,
  Eye, Video, Image as ImageIcon
} from 'lucide-react';
import ExerciseDetailModal from './ExerciseDetailModal';

interface ProgramDayManagerProps {
  program: ExerciseProgram;
  availableExercises: Exercise[];
  onUpdateProgram: (updated: ExerciseProgram) => void;
  onStartGuidedWorkout: (day: ProgramDay) => void;
  onScheduleDay: (day: ProgramDay) => void;
  record?: DailyRecord;
  onUpdateRecord?: (updatedRecord: DailyRecord) => void;
}

export default function ProgramDayManager({
  program,
  availableExercises,
  onUpdateProgram,
  onStartGuidedWorkout,
  onScheduleDay,
  record,
  onUpdateRecord
}: ProgramDayManagerProps) {
  const { t, lang } = useLanguage();

  const [expandedDayId, setExpandedDayId] = useState<string | null>(
    program.days?.[0]?.id || null
  );

  // Editing state for day
  const [editingDay, setEditingDay] = useState<ProgramDay | null>(null);
  const [dayTitleInput, setDayTitleInput] = useState('');
  const [dayNotesInput, setDayNotesInput] = useState('');
  const [dayExercises, setDayExercises] = useState<ProgramDayExercise[]>([]);
  
  // Exercise Selector / JSON editor tabs inside day edit modal
  const [editorTab, setEditorTab] = useState<'pick' | 'custom' | 'json'>('pick');
  const [rawDayJson, setRawDayJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Custom single exercise form state inside Day Editor
  const [customEx, setCustomEx] = useState<Partial<ProgramDayExercise>>({
    name: '',
    purpose: '',
    targetMuscles: '',
    sets: 3,
    reps: '10',
    rest: '20',
    duration: 5,
    difficulty: 'Beginner',
    description: ''
  });

  // Complete exercise detail view modal
  const [selectedDetailExercise, setSelectedDetailExercise] = useState<Exercise | null>(null);

  const getResolvedExercise = (ex: ProgramDayExercise): Exercise => {
    const match = availableExercises.find(a => 
      (ex.exerciseId && a.id === ex.exerciseId) || 
      (ex.id && a.id === ex.id) || 
      (a.name && ex.name && a.name.trim().toLowerCase() === ex.name.trim().toLowerCase())
    );
    if (!match) {
      return {
        id: ex.id || ex.exerciseId || `prog_ex_${Date.now()}`,
        name: ex.name,
        category: ex.category || 'rehab',
        description: ex.description || '',
        purpose: ex.purpose || '',
        targetMuscles: ex.targetMuscles || '',
        sets: ex.sets || 3,
        reps: ex.reps || '10',
        rest: ex.rest || '20',
        duration: ex.duration || 5,
        difficulty: ex.difficulty || 'Beginner',
        precautions: ex.precautions || '',
        equipment: ex.equipment || '',
        image: ex.image,
        videoUrl: ex.videoUrl,
        isCustom: ex.isCustom,
        scope: ex.scope || 'personal'
      };
    }
    return {
      ...match,
      name: ex.name || match.name,
      description: (ex.description && ex.description.trim()) ? ex.description : (match.description || ''),
      purpose: (ex.purpose && ex.purpose.trim()) ? ex.purpose : (match.purpose || ''),
      targetMuscles: ex.targetMuscles || match.targetMuscles || '',
      precautions: ex.precautions || match.precautions || '',
      equipment: ex.equipment || match.equipment || '',
      sets: ex.sets !== undefined ? ex.sets : match.sets,
      reps: ex.reps !== undefined ? ex.reps : match.reps,
      rest: ex.rest !== undefined ? ex.rest : match.rest,
      duration: ex.duration !== undefined ? ex.duration : match.duration,
      difficulty: ex.difficulty || match.difficulty,
      image: ex.image || match.image || (match as any).image_url || undefined,
      videoUrl: ex.videoUrl || match.videoUrl || (match as any).video_url || undefined,
      scope: match.scope || ((match as any).user_id ? 'personal' : 'global'),
      isCustom: match.isCustom || Boolean((match as any).user_id)
    };
  };

  const handleStartAddDay = () => {
    const nextDayNum = (program.days?.length || 0) + 1;
    const newDay: ProgramDay = {
      id: `day_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      programId: program.id,
      dayNumber: nextDayNum,
      title: t('dayDefaultTitle', 'روز {{day}} — ریکاوری و تقویت', { day: formatNumberByLang(nextDayNum, lang) }),
      notes: '',
      exercises: []
    };
    setEditingDay(newDay);
    setDayTitleInput(newDay.title);
    setDayNotesInput('');
    setDayExercises([]);
    setRawDayJson(JSON.stringify([], null, 2));
    setEditorTab('pick');
  };

  const handleStartEditDay = (day: ProgramDay) => {
    setEditingDay(day);
    setDayTitleInput(day.title);
    setDayNotesInput(day.notes || '');
    setDayExercises([...(day.exercises || [])]);
    setRawDayJson(JSON.stringify(day.exercises || [], null, 2));
    setEditorTab('pick');
  };

  const handleSaveDay = () => {
    if (!editingDay) return;
    setJsonError(null);

    let finalExercises = dayExercises;

    if (editorTab === 'json') {
      try {
        const parsed = JSON.parse(rawDayJson);
        if (!Array.isArray(parsed)) {
          setJsonError(t('jsonArrayError', 'فرمت JSON باید یک آرایه (Array) از تمرینات باشد.'));
          return;
        }
        finalExercises = parsed;
      } catch (e: any) {
        setJsonError(t('programJsonSyntaxError', `خطای نحوی در JSON: ${e.message}`, { err: e.message }));
        return;
      }
    }

    const updatedDay: ProgramDay = {
      ...editingDay,
      title: dayTitleInput.trim() || t('dayNumberFallback', 'روز {{day}}', { day: formatNumberByLang(editingDay.dayNumber, lang) }),
      notes: dayNotesInput.trim(),
      exercises: finalExercises
    };

    const existingIndex = program.days.findIndex(d => d.id === editingDay.id);
    let newDaysList: ProgramDay[];

    if (existingIndex >= 0) {
      newDaysList = [...program.days];
      newDaysList[existingIndex] = updatedDay;
    } else {
      newDaysList = [...program.days, updatedDay];
    }

    const updatedProgram: ExerciseProgram = {
      ...program,
      days: newDaysList
    };

    onUpdateProgram(updatedProgram);
    setEditingDay(null);
  };

  const handleDeleteDay = (dayId: string) => {
    if (confirm(t('confirmDeleteDay', 'آیا از حذف این روز تمرینی اطمینان دارید؟'))) {
      const filteredDays = program.days.filter(d => d.id !== dayId);
      // re-index dayNumbers
      const reindexed = filteredDays.map((d, idx) => ({ ...d, dayNumber: idx + 1 }));
      onUpdateProgram({ ...program, days: reindexed });
    }
  };

  const handleRemoveExerciseFromDay = (e: React.MouseEvent, day: ProgramDay, exerciseIdx: number, exerciseName: string) => {
    e.stopPropagation();
    const confirmMsg = t(
      'confirmRemoveExerciseFromDay',
      'آیا از حذف حرکت "{{name}}" از {{dayTitle}} اطمینان دارید؟',
      { name: exerciseName, dayTitle: day.title }
    );
    if (window.confirm(confirmMsg)) {
      const updatedExercises = (day.exercises || []).filter((_, i) => i !== exerciseIdx);
      const updatedDay = { ...day, exercises: updatedExercises };
      const updatedDaysList = program.days.map(d => d.id === day.id ? updatedDay : d);
      onUpdateProgram({ ...program, days: updatedDaysList });
    }
  };

  const handleToggleExerciseCompletion = (e: React.MouseEvent, day: ProgramDay, exerciseIdx: number) => {
    e.stopPropagation();
    const updatedExercises = [...(day.exercises || [])];
    const targetEx = updatedExercises[exerciseIdx];
    const currentCompleted = Boolean(
      targetEx.isCompleted ||
      (targetEx.id && record?.rehabCompletedIds?.includes(targetEx.id)) ||
      (targetEx.exerciseId && record?.rehabCompletedIds?.includes(targetEx.exerciseId))
    );
    const newIsCompleted = !currentCompleted;

    updatedExercises[exerciseIdx] = {
      ...targetEx,
      isCompleted: newIsCompleted
    };
    const updatedDay = { ...day, exercises: updatedExercises };
    const updatedDaysList = program.days.map(d => d.id === day.id ? updatedDay : d);
    onUpdateProgram({ ...program, days: updatedDaysList });

    if (record && onUpdateRecord) {
      const exKey = targetEx.id || targetEx.exerciseId;
      if (exKey) {
        let nextCompleted = [...(record.rehabCompletedIds || [])];
        if (newIsCompleted) {
          if (!nextCompleted.includes(exKey)) nextCompleted.push(exKey);
        } else {
          nextCompleted = nextCompleted.filter(id => id !== exKey && id !== targetEx.id && id !== targetEx.exerciseId);
        }
        onUpdateRecord({ ...record, rehabCompletedIds: nextCompleted });
      }
    }
  };

  const handleMoveExercise = (e: React.MouseEvent, day: ProgramDay, exerciseIdx: number, direction: 'up' | 'down') => {
    e.stopPropagation();
    const updatedExercises = [...(day.exercises || [])];
    if (direction === 'up' && exerciseIdx > 0) {
      const temp = updatedExercises[exerciseIdx - 1];
      updatedExercises[exerciseIdx - 1] = updatedExercises[exerciseIdx];
      updatedExercises[exerciseIdx] = temp;
    } else if (direction === 'down' && exerciseIdx < updatedExercises.length - 1) {
      const temp = updatedExercises[exerciseIdx + 1];
      updatedExercises[exerciseIdx + 1] = updatedExercises[exerciseIdx];
      updatedExercises[exerciseIdx] = temp;
    }
    const updatedDay = { ...day, exercises: updatedExercises };
    const updatedDaysList = program.days.map(d => d.id === day.id ? updatedDay : d);
    onUpdateProgram({ ...program, days: updatedDaysList });
  };

  const handleToggleExerciseInDay = (ex: Exercise) => {
    const exists = dayExercises.some(e => e.name === ex.name || (e.exerciseId && e.exerciseId === ex.id));
    if (exists) {
      setDayExercises(dayExercises.filter(e => e.name !== ex.name && e.exerciseId !== ex.id));
    } else {
      const newEx: ProgramDayExercise = {
        exerciseId: ex.id,
        name: ex.name,
        category: ex.category,
        description: ex.description,
        purpose: ex.purpose,
        targetMuscles: ex.targetMuscles,
        sets: ex.sets,
        reps: ex.reps,
        rest: ex.rest,
        duration: ex.duration,
        difficulty: ex.difficulty,
        image: ex.image,
        videoUrl: ex.videoUrl,
        isCustom: ex.isCustom
      };
      setDayExercises([...dayExercises, newEx]);
    }
  };

  const handleAddCustomExerciseToDay = () => {
    if (!customEx.name?.trim()) return;

    const newExItem: ProgramDayExercise = {
      name: customEx.name.trim(),
      category: customEx.category || 'rehab',
      description: customEx.description?.trim() || '',
      purpose: customEx.purpose?.trim() || '',
      targetMuscles: customEx.targetMuscles?.trim() || '',
      sets: customEx.sets || 3,
      reps: customEx.reps || '10',
      rest: customEx.rest || '20',
      duration: customEx.duration || 5,
      difficulty: customEx.difficulty || 'Beginner',
      isCustom: true
    };

    setDayExercises([...dayExercises, newExItem]);
    setCustomEx({
      name: '',
      purpose: '',
      targetMuscles: '',
      sets: 3,
      reps: '10',
      rest: '20',
      duration: 5,
      difficulty: 'Beginner',
      description: ''
    });
  };

  return (
    <div className="space-y-4">
      
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40 rounded-2xl border border-purple-200 dark:border-purple-800/40">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-purple-600" />
            <span>{t('manageProgramDaysTitle', 'مدیریت روزهای برنامه‌ی «{{title}}»', { title: program.title })}</span>
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('totalDaysDesigned', 'مجموع روزهای طراحی‌شده: {{count}} روز', { count: program.days?.length || 0 })}
          </p>
        </div>

        <button
          onClick={handleStartAddDay}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-md flex items-center gap-2 transition-all cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>{t('addNewProgramDayBtn', 'افزودن روز تمرینی جدید')}</span>
        </button>
      </div>

      {/* Days List */}
      {!program.days || program.days.length === 0 ? (
        <div className="p-8 text-center bg-gray-50 dark:bg-gray-800/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {t('noDaysInProgramYet', 'هنوز روز تمرینی برای این برنامه ثبت نشده است.')}
          </p>
          <button
            onClick={handleStartAddDay}
            className="mt-3 px-4 py-2 bg-purple-600 text-white text-xs font-bold rounded-xl cursor-pointer hover:bg-purple-700 transition-colors inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>{t('addNewProgramDayBtn', 'افزودن روز تمرینی جدید')}</span>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {program.days.map((day) => {
            const isExpanded = expandedDayId === day.id;

            return (
              <div
                key={day.id}
                className="bg-white dark:bg-gray-800/90 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-hidden transition-all"
              >
                {/* Day Summary Bar */}
                <div className="p-4 flex flex-wrap items-center justify-between gap-3 bg-gray-50/70 dark:bg-gray-800/50">
                  <div 
                    onClick={() => setExpandedDayId(isExpanded ? null : day.id)}
                    className="flex items-center gap-3 cursor-pointer flex-1 min-w-[200px]"
                  >
                    <div className="w-8 h-8 rounded-xl bg-purple-600 text-white font-bold text-sm flex items-center justify-center shadow-xs">
                      {day.dayNumber}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
                        {day.title}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {day.exercises?.length || 0} {t('rehabExLabel', 'حرکت ورزشی')} • {day.notes || t('noProgramDesc', 'بدون توضیحات تکمیلی')}
                      </p>
                    </div>
                  </div>

                  {/* Day Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Guided Voice Workout Button */}
                    <button
                      onClick={() => onStartGuidedWorkout(day)}
                      disabled={!day.exercises || day.exercises.length === 0}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-xl shadow-xs flex items-center gap-1.5 transition-all cursor-pointer"
                      title={t('runGuidedWorkoutForDay', 'اجرای تمرین روز')}
                    >
                      <Play className="w-3.5 h-3.5 fill-current" />
                      <span>{t('runWorkoutForDay', 'اجرای تمرین این روز')}</span>
                    </button>

                    {/* Schedule to Weekly Routine Button */}
                    <button
                      onClick={() => onScheduleDay(day)}
                      className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 text-xs font-semibold rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/60 flex items-center gap-1.5 transition-colors cursor-pointer"
                      title={t('scheduleDayInPlanner', 'زمان‌بندی در برنامه‌ریز')}
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{t('scheduleDayBtn', 'افزودن به روتین هفتگی')}</span>
                    </button>

                    {/* Edit Day Button */}
                    <button
                      onClick={() => handleStartEditDay(day)}
                      className="p-1.5 text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/50 rounded-lg transition-colors cursor-pointer"
                      title={t('editDayModalTitle', 'ویرایش روز {{day}}', { day: day.dayNumber })}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>

                    {/* Delete Day Button */}
                    <button
                      onClick={() => handleDeleteDay(day.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-lg transition-colors cursor-pointer"
                      title={t('deleteRecord', 'حذف روز')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    {/* Expand/Collapse Chevron */}
                    <button
                      onClick={() => setExpandedDayId(isExpanded ? null : day.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded Exercises Detail */}
                {isExpanded && (
                  <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-800 space-y-3">
                    {day.notes && (
                      <div className="p-3 bg-amber-50/70 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl text-xs text-amber-800 dark:text-amber-300">
                        💡 <strong>توضیحات روز:</strong> {day.notes}
                      </div>
                    )}

                    {!day.exercises || day.exercises.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">هیچ حرکتی در این روز ثبت نشده است. روی دکمه ویرایش کلیک کنید.</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {day.exercises.map((ex, idx) => {
                          const resolved = getResolvedExercise(ex);
                          const isExCompleted = Boolean(
                            ex.isCompleted ||
                            (ex.id && record?.rehabCompletedIds?.includes(ex.id)) ||
                            (ex.exerciseId && record?.rehabCompletedIds?.includes(ex.exerciseId))
                          );
                          return (
                            <div
                              key={idx}
                              onClick={() => setSelectedDetailExercise(resolved)}
                              className={`group relative p-3 rounded-xl border flex items-start gap-3 transition-all cursor-pointer shadow-xs hover:shadow-md ${
                                isExCompleted
                                  ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800/80'
                                  : 'bg-gray-50/90 dark:bg-gray-900/60 hover:bg-purple-50/60 dark:hover:bg-purple-950/20 border-gray-200 dark:border-gray-700/80 hover:border-purple-300 dark:hover:border-purple-800/80'
                              }`}
                            >
                              {resolved.image ? (
                                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-200 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                  <img 
                                    src={resolved.image} 
                                    alt={resolved.name} 
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    referrerPolicy="no-referrer"
                                  />
                                </div>
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                                  {idx + 1}
                                </div>
                              )}

                              <div className="flex-1 min-w-0 text-xs space-y-1">
                                <div className="flex items-center justify-between gap-1.5">
                                  <div className="flex items-center gap-2 truncate">
                                    <button 
                                      type="button" 
                                      onClick={(e) => handleToggleExerciseCompletion(e, day, idx)}
                                      className={`p-1 rounded-md border transition-colors cursor-pointer shrink-0 ${isExCompleted ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-emerald-500 text-transparent'}`}
                                      title={isExCompleted ? t('markAsIncomplete', 'ثبت به عنوان انجام نشده') : t('markAsCompleted', 'ثبت به عنوان انجام شده')}
                                    >
                                      <Check className="w-3 h-3" />
                                    </button>
                                    <h5 className={`font-bold truncate transition-colors ${isExCompleted ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400'}`}>
                                      {resolved.name}
                                    </h5>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button 
                                      onClick={(e) => handleMoveExercise(e, day, idx, 'up')} 
                                      disabled={idx === 0} 
                                      className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-30 cursor-pointer"
                                      title={t('moveUp', 'انتقال به بالا')}
                                    >
                                      <ChevronUp className="w-3.5 h-3.5"/>
                                    </button>
                                    <button 
                                      onClick={(e) => handleMoveExercise(e, day, idx, 'down')} 
                                      disabled={idx === day.exercises.length - 1} 
                                      className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-30 cursor-pointer"
                                      title={t('moveDown', 'انتقال به پایین')}
                                    >
                                      <ChevronDown className="w-3.5 h-3.5"/>
                                    </button>

                                    {resolved.videoUrl && (
                                      <span className="p-1 rounded-md bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400 text-[10px]" title={t('hasVideoInstruction', 'دارای ویدیو آموزشی')}>
                                        <Video className="w-3 h-3" />
                                      </span>
                                    )}
                                    {resolved.image && !resolved.videoUrl && (
                                      <span className="p-1 rounded-md bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 text-[10px]" title={t('hasImageInstruction', 'دارای تصویر راهنما')}>
                                        <ImageIcon className="w-3 h-3" />
                                      </span>
                                    )}
                                    <span 
                                      className="p-1 text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-300 transition-colors"
                                      title={t('viewCompleteExerciseDetails', 'مشاهده جزئیات کامل و رسانه حرکت')}
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => handleRemoveExerciseFromDay(e, day, idx, resolved.name)}
                                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 rounded-md transition-colors cursor-pointer z-10"
                                      title={t('removeFromProgramDay', 'حذف حرکت از برنامه این روز')}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>

                                <p className="text-gray-500 dark:text-gray-400 text-[11px] line-clamp-1">
                                  {resolved.purpose || resolved.targetMuscles || resolved.description || t('clickForCompleteDetails', 'کلیک برای مشاهده جزئیات کامل')}
                                </p>

                                <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-gray-600 dark:text-gray-300 pt-1 font-mono">
                                  <span className="px-2 py-0.5 bg-gray-200/80 dark:bg-gray-800 rounded-md">
                                    ست: {resolved.sets || 3}
                                  </span>
                                  <span className="px-2 py-0.5 bg-gray-200/80 dark:bg-gray-800 rounded-md">
                                    تکرار: {resolved.reps || '10'}
                                  </span>
                                  <span className="px-2 py-0.5 bg-gray-200/80 dark:bg-gray-800 rounded-md">
                                    استراحت: {resolved.rest || '20s'}
                                  </span>
                                  {resolved.scope && (
                                    <span className={`px-1.5 py-0.5 text-[9px] rounded-md font-sans ${
                                      resolved.scope === 'global' 
                                        ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800' 
                                        : 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                                    }`}>
                                      {resolved.scope === 'global' ? 'عمومی' : 'شخصی'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}

      {/* DAY EDIT / MODAL FORM */}
      {editingDay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-purple-200 dark:border-purple-900/40 w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-purple-600" />
                <span>{t('editDayModalTitle', 'ویرایش روز {{day}}', { day: editingDay.dayNumber })} — {program.title}</span>
              </h3>
              <button
                onClick={() => setEditingDay(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    {t('dayTitleInputLabel', 'عنوان روز تمرینی:')}
                  </label>
                  <input
                    type="text"
                    value={dayTitleInput}
                    onChange={(e) => setDayTitleInput(e.target.value)}
                    placeholder={t('dayTitleInputPh', 'مثلاً: روز ۱ — کشش پسواس و دکومپرشن')}
                    className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    {t('dayNotesInputLabel', 'یادداشت‌ها و راهنمای تمرینی این روز:')}
                  </label>
                  <input
                    type="text"
                    value={dayNotesInput}
                    onChange={(e) => setDayNotesInput(e.target.value)}
                    placeholder={t('dayNotesPlaceholder', 'مثلاً: انجام تمرینات در ۲ نوبت صبح و عصر...')}
                    className="w-full p-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Sub-tabs for exercise selection / creation / JSON */}
              <div className="flex border-b border-gray-200 dark:border-gray-700 gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => setEditorTab('pick')}
                  className={`pb-2 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                    editorTab === 'pick'
                      ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('tabPickFromLibrary', 'انتخاب از بانک حرکات')} ({availableExercises.length})
                </button>
                <button
                  type="button"
                  onClick={() => setEditorTab('custom')}
                  className={`pb-2 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                    editorTab === 'custom'
                      ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t('tabAddCustomExercise', 'تعریف حرکت اختصاصی')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditorTab('json');
                    setRawDayJson(JSON.stringify(dayExercises, null, 2));
                  }}
                  className={`pb-2 text-xs font-bold border-b-2 transition-colors cursor-pointer flex items-center gap-1 ${
                    editorTab === 'json'
                      ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FileCode className="w-3.5 h-3.5" />
                  <span>{t('tabJsonEditor', 'ویرایش متنی JSON')}</span>
                </button>
              </div>

              {/* TAB 1: Pick from Library */}
              {editorTab === 'pick' && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('selectedExercisesCount', 'حرکات انتخاب‌شده برای این روز ({{count}} مورد):', { count: dayExercises.length })}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 max-h-60 overflow-y-auto p-1">
                    {availableExercises.map((ex) => {
                      const isSelected = dayExercises.some(e => e.name === ex.name || e.exerciseId === ex.id);

                      return (
                        <div
                          key={ex.id}
                          onClick={() => handleToggleExerciseInDay(ex)}
                          className={`p-3 rounded-xl border text-xs cursor-pointer transition-all flex items-center justify-between ${
                            isSelected
                              ? 'bg-purple-50 dark:bg-purple-950/40 border-purple-300 dark:border-purple-700 text-purple-900 dark:text-purple-200 shadow-xs'
                              : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-300'
                          }`}
                        >
                          <div>
                            <span className="font-bold">{ex.name}</span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 block mt-0.5">
                              {ex.targetMuscles || ex.purpose}
                            </span>
                          </div>
                          <div className={`w-5 h-5 rounded-md flex items-center justify-center ${
                            isSelected ? 'bg-purple-600 text-white' : 'border border-gray-300 dark:border-gray-600'
                          }`}>
                            {isSelected && <Check className="w-3.5 h-3.5" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TAB 2: Custom Exercise Form */}
              {editorTab === 'custom' && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('addCustomExNameLabel', 'نام حرکت سفارشی:')}
                      </label>
                      <input
                        type="text"
                        value={customEx.name}
                        onChange={(e) => setCustomEx({ ...customEx, name: e.target.value })}
                        placeholder={t('addCustomExNamePh', 'مثلاً: کشش فیله با کش')}
                        className="w-full p-2 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('addCustomExTargetLabel', 'عضلات هدف:')}
                      </label>
                      <input
                        type="text"
                        value={customEx.targetMuscles}
                        onChange={(e) => setCustomEx({ ...customEx, targetMuscles: e.target.value })}
                        placeholder={t('addCustomExTargetPh', 'مثلاً: فیله کمر، همسترینگ')}
                        className="w-full p-2 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('addCustomExSetsLabel', 'تعداد ست:')}
                      </label>
                      <input
                        type="number"
                        value={customEx.sets}
                        onChange={(e) => setCustomEx({ ...customEx, sets: Number(e.target.value) })}
                        className="w-full p-2 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('addCustomExRepsLabel', 'تکرار / مکث:')}
                      </label>
                      <input
                        type="text"
                        value={customEx.reps}
                        onChange={(e) => setCustomEx({ ...customEx, reps: e.target.value })}
                        placeholder={t('addCustomExRepsPh', 'مثلاً: ۱۰ تکرار')}
                        className="w-full p-2 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {t('addCustomExRestLabel', 'استراحت بین ست‌ها:')}
                      </label>
                      <input
                        type="text"
                        value={customEx.rest}
                        onChange={(e) => setCustomEx({ ...customEx, rest: e.target.value })}
                        placeholder={t('addCustomExRestPh', 'مثلاً: ۲۰ ثانیه')}
                        className="w-full p-2 text-xs bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddCustomExerciseToDay}
                    disabled={!customEx.name?.trim()}
                    className="px-4 py-2 bg-purple-600 text-white text-xs font-bold rounded-lg cursor-pointer disabled:opacity-50 hover:bg-purple-700 transition-colors"
                  >
                    + {t('addExerciseToDayBtn', 'افزودن این حرکت به روز تمرینی')}
                  </button>
                </div>
              )}

              {/* TAB 3: JSON Editor */}
              {editorTab === 'json' && (
                <div className="space-y-2">
                  {jsonError && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs rounded-xl flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <div>{jsonError}</div>
                    </div>
                  )}
                  <textarea
                    rows={8}
                    value={rawDayJson}
                    onChange={(e) => setRawDayJson(e.target.value)}
                    className="w-full p-3 font-mono text-xs bg-gray-950 text-emerald-400 rounded-xl border border-gray-800 dir-ltr text-left"
                  />
                </div>
              )}

              {/* Chosen exercises overview in day modal */}
              {editorTab !== 'json' && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <h5 className="text-xs font-bold text-gray-800 dark:text-gray-200 mb-2">
                    {t('exercisesInDayHeading', 'حرکات ثبت‌شده برای روز {{day}} ({{count}} حرکت):', { day: editingDay.dayNumber, count: dayExercises.length })}
                  </h5>
                  {dayExercises.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">{t('noExercisesSelectedForDay', 'هنوز هیچ حرکتی برای این روز انتخاب نشده است.')}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {dayExercises.map((ex, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 text-xs rounded-lg flex items-center gap-1.5"
                        >
                          <span>{ex.name}</span>
                          <button
                            type="button"
                            onClick={() => setDayExercises(dayExercises.filter((_, i) => i !== idx))}
                            className="text-purple-400 hover:text-red-600 cursor-pointer"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
              <button
                type="button"
                onClick={() => setEditingDay(null)}
                className="px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
              >
                {t('cancel', 'انصراف')}
              </button>
              <button
                type="button"
                onClick={handleSaveDay}
                className="px-5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl shadow-md transition-all cursor-pointer"
              >
                {t('saveDayChangesBtn', 'ذخیره تغییرات روز')}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Complete Exercise Detail Modal (Video, Image, Instructions, Precautions, Muscles, Sets/Reps) */}
      {selectedDetailExercise && (
        <ExerciseDetailModal
          isOpen={!!selectedDetailExercise}
          onClose={() => setSelectedDetailExercise(null)}
          exercise={selectedDetailExercise}
          onStartWorkout={(ex) => {
            const parentDay = program.days?.find(d => 
              d.exercises?.some(e => (e.id && e.id === ex.id) || e.name === ex.name || (e.exerciseId && e.exerciseId === ex.id))
            );
            if (parentDay) {
              onStartGuidedWorkout(parentDay);
              setSelectedDetailExercise(null);
            }
          }}
        />
      )}

    </div>
  );
}
